import React, { FC, useState } from 'react';
import { formatDate } from 'utils/format';
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import CircleIcon from '@mui/icons-material/Circle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { useTranslation } from 'react-i18next';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import PlanOverviewDialog from 'components/shared/production-scheduling/plan-overview-dialog/plan-overview-dialog.component';

type DetailOrdersTableStep = ScheduledStep & { totalProductionHours?: number };

type DetailOrdersTableProps = {
    steps: DetailOrdersTableStep[];
    date?: Date;
    onStepChanged?: () => void;
};

const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const STATUS_LABEL_KEYS: Record<ScheduleStatus, string> = {
    [ScheduleStatus.LATE]: 'STATUS_LATE',
    [ScheduleStatus.EARLY]: 'STATUS_DONE',
    [ScheduleStatus.ON_SCHEDULE]: 'STATUS_ON_SCHEDULE',
    [ScheduleStatus.PAUSED]: 'STATUS_PAUSED',
};

const STATUS_COLORS: Record<ScheduleStatus, string> = {
    [ScheduleStatus.LATE]: '#d32f2f',
    [ScheduleStatus.EARLY]: '#2e7d32',
    [ScheduleStatus.ON_SCHEDULE]: 'text.primary',
    [ScheduleStatus.PAUSED]: 'warning.main',
};

const lastActionKey = (lastActionType?: string): string =>
    lastActionType === 'pause' ? 'PAUSED_BY' : lastActionType === 'resume' ? 'RESUMED_BY' : 'MOVED_BY';

const DetailOrdersTable: FC<DetailOrdersTableProps> = ({ steps, date, onStepChanged }) => {
    const { t: translate } = useTranslation(['production-dashboard', 'orders']);
    const [overviewOrderId, setOverviewOrderId] = useState<string | null>(null);
    const isToday = date ? isSameDay(date, new Date()) : false;

    if (steps.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('production-dashboard:NO_SCHEDULED_STEPS')}
            </Typography>
        );
    }

    return (
        <>
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('production-dashboard:ORDER_ID')}</TableCell>
                            <TableCell>{translate('production-dashboard:CURRENT_STEP')}</TableCell>
                            <TableCell>{translate('production-dashboard:CUSTOMER')}</TableCell>
                            <TableCell>{translate('production-dashboard:PATIENT')}</TableCell>
                            <TableCell>{translate('production-dashboard:PRODUCTION_HOURS')}</TableCell>
                            <TableCell>{translate('production-dashboard:START_DATE')}</TableCell>
                            <TableCell>{translate('production-dashboard:DEADLINE')}</TableCell>
                            <TableCell>{translate('production-dashboard:STATUS')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {steps.map((step, index) => {
                            const statusLabel = translate(
                                `production-dashboard:${STATUS_LABEL_KEYS[step.scheduleStatus]}`,
                            );
                            const statusColor = STATUS_COLORS[step.scheduleStatus];
                            return (
                                <TableRow
                                    key={`${step.orderId}-${step.stepId}-${index}`}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => window.open(`/order-details/${step.orderId}/main-view`, '_blank')}
                                >
                                    <TableCell>{step.orderDisplayId || step.orderId}</TableCell>
                                    <TableCell>
                                        {isToday && (
                                            <Tooltip
                                                title={
                                                    step.scheduleStatus === ScheduleStatus.LATE
                                                        ? translate('production-dashboard:STATUS_LATE')
                                                        : step.workStarted
                                                          ? translate('production-dashboard:STATUS_WORK_STARTED')
                                                          : translate('production-dashboard:STATUS_WAITING')
                                                }
                                            >
                                                <CircleIcon
                                                    color={
                                                        step.scheduleStatus === ScheduleStatus.LATE
                                                            ? 'error'
                                                            : step.workStarted
                                                              ? 'success'
                                                              : undefined
                                                    }
                                                    sx={{
                                                        fontSize: 10,
                                                        mr: 0.75,
                                                        verticalAlign: 'middle',
                                                        ...(!step.workStarted &&
                                                            step.scheduleStatus !== ScheduleStatus.LATE && {
                                                                color: '#fbc02d',
                                                            }),
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                        {step.currentStepName ?? '-'}
                                        {step.isPaused && (
                                            <Tooltip title={translate('production-dashboard:STATUS_PAUSED')}>
                                                <PauseCircleIcon
                                                    color="warning"
                                                    sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }}
                                                />
                                            </Tooltip>
                                        )}
                                        {step.isPaused && step.pauseReason && (
                                            <Typography
                                                variant="caption"
                                                color="warning.dark"
                                                display="block"
                                                data-test="detail-orders-table.pause-reason"
                                            >
                                                {step.pauseReason}
                                            </Typography>
                                        )}
                                        {step.lastActionByName && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {translate(
                                                    `production-dashboard:${lastActionKey(step.lastActionType)}`,
                                                    {
                                                        name: step.lastActionByName,
                                                    },
                                                )}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {step.deliveryClinicName || step.clinicName || '-'}
                                        {/* The space keeps clinic and dentist apart in copied text (DD-3802). */}
                                        {step.dentistName && ' '}
                                        {step.dentistName && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {step.dentistName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{step.patientName || '-'}</TableCell>
                                    <TableCell>
                                        {step.totalProductionHours
                                            ? `${step.totalProductionHours.toFixed(2)}h`
                                            : step.estimatedProductionHours
                                              ? `${step.estimatedProductionHours.toFixed(2)}h`
                                              : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {step.startDate ? formatDate(new Date(step.startDate), 'datetime') : '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(new Date(step.deadline), 'datetime')}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: statusColor }}>
                                            {statusLabel}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={translate('orders:SHOW_PLAN_OVERVIEW')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOverviewOrderId(step.orderId);
                                                }}
                                            >
                                                <TimelineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <PlanOverviewDialog
                open={!!overviewOrderId}
                onClose={() => setOverviewOrderId(null)}
                orderId={overviewOrderId || undefined}
                withWorkflowActions
                onStepChanged={onStepChanged}
            />
        </>
    );
};

export default DetailOrdersTable;
